// React and 3P
import { googleLogout, useGoogleLogin } from '@react-oauth/google';
import CryptoJS from 'crypto-js';
import hmacSHA512 from 'crypto-js/hmac-sha512';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Common and Widgets
import Configs from '@/Configs';
import {
  getLoginBrandDisplay,
  persistLoginBrandCache,
  readLoginBrandCache,
} from '@/authentication/core/loginBrandCache.js';
import { default as Footer } from '@/common/Footer';
import * as apiCalls from '@/common/apiCalls';
import { useAuth } from '@/common/appContext';
import { useData } from '@/common/dataContext';
import { default as Loader } from '@/common/support/loader';
import { default as Toaster } from '@/common/support/toaster';
import { hexToRGBA } from '@/common/utils/colorUtils.js';
import KincodeButton from '@/components/button/KincodeButton';
import { default as UnifiedIcon } from '@/components/UnifiedIcon';
import { useToastState } from '@/common/uiShellContext';

// ---------------------------------------------------------------------------------------------------
function Login(props) {
  const { t } = useTranslation();

  // Variables set up
  const { setToken, setAuthenticated, isAuthenticationLoading, setIsAuthenticationLoading } =
    useAuth();
  const {
    companyID,
    setCompanyID,
    employeeID,
    setEmployeeID,
    setSecurityLevel,
    setHrbpModuleAccess,
  } = useAuth();
  const { addToast, getProfile, setProfile, setCompanyPreferences, setCompanyBackgroundImageURL } = useData();
  const { toasterMessage } = useToastState();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const [missingUsername, setMissingUsername] = useState(false);
  const [wrongPassword, setWrongPassword] = useState(false);
  const [missingInput, setMissingInput] = useState(false);
  const [wrongUsernameFormat, setWrongUsernameFormat] = useState(false);
  const [emptyUsername, setEmptyUsername] = useState(false);

  const [showPasswordResetMessage, setShowPasswordResetMessage] = useState(false);

  const loginBrandDisplay = useMemo(
    () => getLoginBrandDisplay(readLoginBrandCache()),
    []
  );
  const [loginLogoSrc, setLoginLogoSrc] = useState(loginBrandDisplay.logoUrl);

  const loginBrandInputStyle = useMemo(
    () => ({
      '--login-input-brand': loginBrandDisplay.colorHex,
      '--login-input-brand-ring': hexToRGBA(loginBrandDisplay.colorHex, 0.5),
      accentColor: loginBrandDisplay.colorHex,
    }),
    [loginBrandDisplay.colorHex]
  );

  const emailRegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // ---------------------------------------------------------------------------------------------------------
  // Google login methods
  // ---------------------------------------------------------------------------------------------------------
  function logInWithGoogle() {
    // Start loader
    setIsAuthenticationLoading(true);
    // Log in with Google
    logInGoogle();
  }

  const logInGoogle = useGoogleLogin({
    onSuccess: (codeResponse) => {
      if (Configs.devEnvironment) console.log('[login.js][logInGoogle()] success: ', codeResponse);
      props?.setIsGoogleAuthenticated(true);
      props?.getGoogleUserInfo(codeResponse.access_token);
    },
    // flow: 'auth-code',
    onError: (error) => {
      if (Configs.devEnvironment) console.error('[login.js][logInGoogle()] error: ', error);
      addToast({ message: t('error'), type: Configs.errorToaster });
      setIsAuthenticationLoading(false);
      setAuthenticated(false);
      props?.setIsGoogleAuthenticated(false);
    },
    onNonOAuthError: (error) => {
      if (Configs.devEnvironment)
        console.error('[login.js][logInGoogle()] onNonOAuthError: ', error);
      addToast({ message: t('error'), type: Configs.errorToaster });
      setIsAuthenticationLoading(false);
      setAuthenticated(false);
      props?.setIsGoogleAuthenticated(false);
    },
  });

  function logOutGoogle() {
    setIsAuthenticationLoading(false);
    setAuthenticated(false);
    props?.setIsGoogleAuthenticated(false);
    googleLogout();
    sessionStorage.clear();
  }

  // ---------------------------------------------------------------------------------------------------
  // Data validation methods
  // ---------------------------------------------------------------------------------------------------
  // Login button was pressed - validate the input
  async function validateInput(event) {
    event.preventDefault();
    setMissingUsername(false);
    setWrongPassword(false);
    setMissingInput(false);
    setWrongUsernameFormat(false);
    setEmptyUsername(false);

    // Validate input
    // TO DO REGEXP EMAIL ONLY
    if (username !== '' && password !== '' && emailRegExp.test(username)) {
      // All okay, proceed to login - clean-up fronted messages

      // Proceed to login
      logIn('bearer');
    } else {
      // missing input - update frontend messaging
      if (username === '' || password === '') setMissingInput(true);
      if (!emailRegExp.test(username)) setWrongUsernameFormat(true);
    }
  }

  // Password forgotten button was pressed
  async function forgotPassword() {
    setMissingUsername(false);
    setWrongPassword(false);
    setMissingInput(false);
    setWrongUsernameFormat(false);
    setEmptyUsername(false);

    // To-Do what to do if an employee forgot their password
    if (username === '' || !emailRegExp.test(username)) {
      if (username === '') setEmptyUsername(true);
      if (!emailRegExp.test(username)) setWrongUsernameFormat(true);
      setShowPasswordResetMessage(false);
    } else {
      setEmptyUsername(false);
      setWrongUsernameFormat(false);
      requestPasswordReset();
    }
  }

  // ---------------------------------------------------------------------------------------------------
  // API calls
  // ---------------------------------------------------------------------------------------------------
  // Request password reset
  async function requestPasswordReset() {
    if (!emailRegExp.test(username)) {
      setWrongUsernameFormat(true);
      return;
    }

    try {
      const result = await apiCalls.resetPassword(
        null,
        null,
        import.meta.env.VITE_UNAUTHENTICATED_REQUESTS_API_TOKEN,
        { username: username }
      );

      if (Configs.devEnvironment)
        console.log('[login.js][requestResetPassword()] result: ', result);

      if (result === 429 || result?.status === 429) {
        const rateLimitMessage =
          (typeof result === 'object' && result?.message) || t('auth.rate-limit.reset');
        addToast({ message: rateLimitMessage, type: Configs.errorToaster });
      } else if (result > 200) {
        addToast({ message: t('password.reset.error'), type: Configs.errorToaster });
        // throw result
      } else {
        // We managed to get a response from server - inform user
        setShowPasswordResetMessage(true);
        addToast({ message: t('password.reset.requested'), type: Configs.successToaster });

        // Token was received, and we navigate to another page
        if (Configs.devEnvironment)
          console.log('[login.js][logIn()] Body from resetPassword: ', result.body);

        setMissingUsername(false);
        setWrongPassword(false);
      }
    } catch (error) {
      addToast({ message: t('error.server-unreachable'), type: Configs.errorToaster });
      console.error('[login.js][requestResetPassword()] Error fetching data: ', error);
    }
  }

  // Login button was pressed
  async function logIn() {
    // Define body
    const requestBody = {
      username: username,
      password: hmacSHA512(password, Configs.privateKey).toString(),
      timestamp: new Date().toISOString(),
    };
    try {
      // Start loader
      setIsAuthenticationLoading(true);

      const response = await apiCalls.logIn(
        companyID,
        employeeID,
        import.meta.env.VITE_UNAUTHENTICATED_REQUESTS_API_TOKEN,
        requestBody
      );

      // Handle response
      // Review response status
      if (response === 500) {
        addToast({ message: t('error.server-unreachable'), type: Configs.errorToaster });
        setAuthenticated(false);
        setIsAuthenticationLoading(false);
      } else if (response === 422) {
        addToast({ message: t('error.unauthenticated'), type: Configs.errorToaster });
        setAuthenticated(false);
        setIsAuthenticationLoading(false);
        // setWrongPassword(true);
      } else if (response === 404) {
        addToast({ message: t('error.not-found'), type: Configs.errorToaster });
        setAuthenticated(false);
        setIsAuthenticationLoading(false);
      } else if (response === 403) {
        addToast({ message: t('error.unauthorized'), type: Configs.errorToaster });
        setAuthenticated(false);
        setIsAuthenticationLoading(false);
      } else if (response === 401) {
        addToast({ message: t('error.unauthenticated'), type: Configs.errorToaster });
        setAuthenticated(false);
        setIsAuthenticationLoading(false);
        sessionStorage.clear();
        setWrongPassword(true);
      } else if (response === 429 || response?.status === 429) {
        const rateLimitMessage =
          (typeof response === 'object' && response?.message) || t('auth.rate-limit.login');
        addToast({ message: rateLimitMessage, type: Configs.errorToaster });
        setAuthenticated(false);
        setIsAuthenticationLoading(false);
        setWrongPassword(false);
      } else if (response === 400) {
        addToast({ message: t('error.bad-request'), type: Configs.errorToaster });
        setAuthenticated(false);
        setIsAuthenticationLoading(false);
      } else {
        if (Configs.devEnvironment) console.log('[logn.js][logIn()] response: ', response);

        // Token was received, and we navigate to another page
        if (response?.bearerToken && response?.companyID && response?.employeeID) {
          if (Configs.devEnvironment)
            console.log('[logn.js][logIn()] requesting profile: ', response);
          if (response?.csrfToken)
            getProfile(
              response?.employeeID,
              response?.companyID,
              response?.bearerToken,
              response?.csrfToken
            );
          if (response?.bearerToken) setToken(response?.bearerToken);
          if (response?.csrfToken)
            sessionStorage.setItem(
              'csrfToken',
              CryptoJS.AES.encrypt(response.csrfToken.toString(), Configs.privateKey).toString()
            );
          setCompanyID(response?.companyID);
          if (response?.companyID)
            sessionStorage.setItem(
              'companyID',
              CryptoJS.AES.encrypt(response.companyID.toString(), Configs.privateKey).toString()
            );
          setEmployeeID(response?.employeeID);
          if (response?.employeeID)
            sessionStorage.setItem(
              'employeeID',
              CryptoJS.AES.encrypt(response.employeeID.toString(), Configs.privateKey).toString()
            );
          if (response?.profile) {
            setProfile(response?.profile);
            if (response?.profile?.roles?.securityLevels)
              setSecurityLevel(response?.profile?.roles?.securityLevels);
            setHrbpModuleAccess(response?.profile?.hrbpModuleAccess ?? null);
            if (response?.profile?.companyPreferences) {
              setCompanyPreferences(response?.profile?.companyPreferences);
              persistLoginBrandCache(
                response?.companyID,
                response?.profile?.companyPreferences
              );
            }
            if (response?.profile?.companyPreferences?.personalization?.backgroundImageURL)
              setCompanyBackgroundImageURL(
                response?.profile?.companyPreferences?.personalization?.backgroundImageURL
              );
          }

          setAuthenticated(true);
          setIsAuthenticationLoading(false);
        }

        // Clear frontend messages
        setMissingUsername(false);
        setWrongPassword(false);
      }
    } catch (error) {
      addToast({ message: t('error.server-unreachable'), type: Configs.errorToaster });
      setAuthenticated(false);
      console.error('[login.js][logIn()] Error fetching data: ', error);
    }
  }
  // ---------------------------------------------------------------------------------------------------
  // Render methods
  // ---------------------------------------------------------------------------------------------------
  return (
    <div
      data-testid="authentication-login"
      data-login-branded={loginBrandDisplay.isBranded ? 'true' : 'false'}
      className="flex flex-col items-center justify-between h-screen w-full bg-white"
    >
      <div
        data-testid="authentication-login-main"
        className={
          loginBrandDisplay.isBranded
            ? 'flex flex-col lg:items-center justify-center w-full h-full'
            : 'flex flex-col lg:items-center justify-center bg-gradient-to-b from-green-200 to-white w-full h-full'
        }
        style={loginBrandDisplay.isBranded ? loginBrandDisplay.mainBackgroundStyle : undefined}
      >
        {loginBrandDisplay.isBranded ? (
          <span
            data-testid="authentication-login-branded"
            className="sr-only"
            aria-hidden="true"
          />
        ) : null}
        <div
          data-testid="authentication-login-toaster"
          className="flex flex-row items-center justify-center w-full"
        >
          {toasterMessage && <Toaster message={toasterMessage} timeout={Configs.toasterTimeout} />}
        </div>

        <div
          data-testid="authentication-login-card"
          className="flex flex-col justify-center bg-white border border-gray-100 shadow-xl m-2 py-20 p-12 lg:px-40 rounded-lg lg:max-w-screen-xl"
        >
          <div className="flex flex-col md:flex-row justify-center items-center">
            <div className="flex container w-[180px] h-[180px]">
              <img
                className=" object-center object-contain"
                alt="Kincode-App-Logo"
                data-testid="authentication-login-logo"
                src={loginLogoSrc}
                onError={() => {
                  if (loginLogoSrc !== Configs.companyLogoLarge) {
                    setLoginLogoSrc(Configs.companyLogoLarge);
                  }
                }}
              />
            </div>
          </div>

          {isAuthenticationLoading ? (
            <Loader />
          ) : (
            <Fragment>
              <div
                data-testid="authentication-login-sso-section"
                className="flex flex-col justify-center items-center mb-4 mt-8 gap-6"
              >
                {props.ssoWorkspaceUnknown ? (
                  <p
                    data-testid="authentication-login-sso-campaign-required"
                    className="w-full rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-left text-sm leading-6 text-amber-950"
                  >
                    {t('login.ssoCampaignRequired')}
                  </p>
                ) : null}
                {/* Microsoft SSO */}

                {props.isAzureAuthenticated ? (
                  <button
                    id="ms-sso-logout"
                    data-testid="authentication-login-microsoft-logout"
                    className="flex flex-row items-center text-center lg:text-left justify-center lg:justify-start w-full p-2 shadow bg-gray-200 hover:bg-gray-300 rounded-lg gap-3"
                    onClick={props.logOutAzure}
                  >
                    <UnifiedIcon name="WindowsLogo" library="phosphor" className="h-6 w-6" />
                    {t('logout')}
                  </button>
                ) : (
                  <button
                    id="ms-sso-login"
                    data-testid="authentication-login-microsoft-login"
                    className="flex flex-row items-center text-center lg:text-left justify-center lg:justify-start w-full p-2 shadow bg-gray-200 hover:bg-gray-300 rounded-lg gap-3"
                    onClick={props.logInAzure}
                  >
                    <UnifiedIcon name="WindowsLogo" library="phosphor" className="h-6 w-6" />
                    {t('login.microsoft')}
                  </button>
                )}

                {/* Google SSO — hidden unless a real client ID is configured */}

                {!Configs.googleSSOEnabled ? null : props.isGoogleAuthenticated ? (
                  <button
                    id="google-sso-logout"
                    data-testid="authentication-login-google-logout"
                    className="flex flex-row items-center text-center lg:text-left justify-center lg:justify-start w-full p-2 shadow bg-gray-200 hover:bg-gray-300 rounded-lg gap-3"
                    onClick={logOutGoogle}
                  >
                    <UnifiedIcon
                      name="GoogleLogo"
                      library="phosphor"
                      weight="bold"
                      className="h-6 w-6"
                    />
                    {t('logout')}
                  </button>
                ) : (
                  <button
                    id="google-sso-login"
                    data-testid="authentication-login-google-login"
                    className="flex flex-row items-center text-center lg:text-left justify-center lg:justify-start w-full p-2 shadow bg-gray-200 hover:bg-gray-300 rounded-lg gap-3"
                    onClick={logInWithGoogle}
                  >
                    <UnifiedIcon
                      name="GoogleLogo"
                      library="phosphor"
                      weight="bold"
                      className="h-6 w-6"
                    />
                    {t('login.google')}
                  </button>
                )}

                <div className="flex flex-row lg:w-1/2 w-full justify-between items-center">
                  <div className="mt-8 border-b border-gray-200 border-1 w-full"> </div>
                  <div className="mt-8 text-center w-full">{t('o')}</div>
                  <div className="mt-8 border-b border-gray-200 border-1 w-full"> </div>
                </div>
              </div>

              <div className="flex justify-center">
                <div
                  className={
                    missingInput ||
                    missingUsername ||
                    wrongPassword ||
                    wrongUsernameFormat ||
                    emptyUsername
                      ? 'block flex italic text-red-500 mb-4 '
                      : 'invisible'
                  }
                >
                  {missingUsername ? t('username.does-not-exist') : null}
                  {wrongPassword ? t('password.incorrect') : null}
                  {missingInput ? t('field.missing') : null}
                  {wrongUsernameFormat ? t('username.format.incorrect') : null}
                  {emptyUsername ? t('username.empty') : null}
                </div>
              </div>

              <form
                onSubmit={validateInput}
                data-testid="authentication-login-form"
                className="flex flex-col justify-center text-left items-center w-full gap-1 my-4"
              >
                <div className="flex flex-col w-full items-start py-2 gap-2">
                  <label className="flex text-xs text-gray-600 "> {t('username')}: </label>
                  <input
                    id="username"
                    data-testid="authentication-login-username-input"
                    type="email"
                    placeholder="example@email.com"
                    className="input-field w-full focus:!border-[var(--login-input-brand)] focus:!ring-[var(--login-input-brand-ring)]"
                    style={loginBrandInputStyle}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    autoComplete="off"
                    value={username}
                  ></input>
                </div>

                <div
                  className={
                    username === '' || username === null
                      ? 'hidden'
                      : 'flex flex-col w-full items-start py-2 gap-2'
                  }
                >
                  <label className="flex text-xs text-gray-600 ">{t('password')}: </label>
                  <input
                    id="password"
                    data-testid="authentication-login-password-input"
                    type="password"
                    placeholder="****"
                    autoComplete="off"
                    className="input-field w-full focus:!border-[var(--login-input-brand)] focus:!ring-[var(--login-input-brand-ring)]"
                    style={loginBrandInputStyle}
                    required
                    onChange={(e) => setPassword(e.target.value)}
                  ></input>
                </div>

                <div
                  className={
                    username === '' || username === null ? 'hidden' : 'w-full m-2'
                  }
                >
                  <KincodeButton
                    id="submit"
                    data-testid="authentication-login-submit-button"
                    type="submit"
                    variant="primary"
                    colorHex={loginBrandDisplay.colorHex}
                    className="w-full"
                  >
                    {t('login')}
                  </KincodeButton>
                </div>
              </form>
              {/* Reset password */}
              <div
                className={
                  username === '' || username === null
                    ? 'hidden'
                    : 'flex flex-col items-center justify-center text-center w-full'
                }
              >
                <div className=" flex flex-col justify-center text-center w-full  rounded m-2 hover:underline ">
                  <KincodeButton
                    id="password-forgot"
                    data-testid="authentication-login-forgot-password-button"
                    type="button"
                    variant="tertiary"
                    colorHex={loginBrandDisplay.colorHex}
                    className="items-center justify-center text-center w-full"
                    onClick={forgotPassword}
                  >
                    {t('password.forgot')}
                  </KincodeButton>
                </div>
              </div>
              <div className="flex w-full flex-col items-center justify-center text-center">
                <KincodeButton
                  type="button"
                  data-testid="plg-login-create-company"
                  variant="tertiary"
                  colorHex={loginBrandDisplay.colorHex}
                  className="w-full items-center justify-center text-center"
                  onClick={() => {
                    window.location.assign(Configs.newCompanyOnboardingURL);
                  }}
                >
                  {t('plg.selfOnboarding.loginCta')}
                </KincodeButton>
              </div>
            </Fragment>
          )}
        </div>
      </div>
      <div
        data-testid="authentication-login-footer"
        className="flex flex-col items-center justify-center w-full"
      >
        <Footer color={'bg-white'} />
      </div>
    </div>
  );
}

export { Login };
export default Login;
